/**
 * The finale's non-Earth bodies: the two spacecraft MapSection's frame loop
 * used to place, plus the lunar globe that replaced the flat crater SVG.
 *
 * They live here rather than in the controller because they are DOM work, not
 * camera work: the controller's job is one camera writer for the Earth, and the
 * moon needs a second MapLibre instance with its own lifecycle. Splitting them
 * also keeps MapSection under its file-size ceiling.
 *
 * Nothing in here owns a clock. `render` is called by the parent's existing
 * scheduler, which hands the spacecraft the angle it already tracks, so they and
 * the Earth can never drift apart, and a hidden tab or reduced-motion preference
 * freezes both at once. The moon needs none of it: its face is locked to one
 * meridian, so it is time-independent by construction. The only timer is the
 * load bound, and it measures visible time.
 */

import type * as MaplibreGl from "maplibre-gl";

import type { ML, Placement } from "@/components/atlas-camera";

/** The palette the finale's bodies are painted with, taken from the page's own tokens. */
export type AtlasBodiesPalette = { surface: string; basin: string; outline: string };

/**
 * One frame of the finale's bodies, in absolute stage pixels.
 *
 * This is `bodyLayout`'s return shape written out rather than derived from it:
 * the render seam is a named contract between the controller and this module,
 * and a consumer should not have to unpack a helper's signature to know what it
 * receives. The two are structurally identical, so the call site
 * (`bodies.render(bodyLayout(...), ...)`) type-checks unchanged.
 */
export type AtlasBodiesLayout = {
  moon: Placement;
  iss: Placement;
  sputnik: Placement;
  /**
   * Whether the moon's disc is inside one lunar radius of the Earth's limb this
   * frame — the pass in which the two slates meet across a few pixels of sky and
   * the caption crosses the planet. The one reason the renderer writes the
   * figure's own dataset: the CSS limb ring hangs on it.
   */
  overEarth: boolean;
};

export type AtlasBodies = {
  render: (layout: AtlasBodiesLayout, closing: number, vendor: ML | null) => void;
  pause: () => void;
  dispose: () => void;
};

/** The LROC mare polygons, built by the map-assets `moon` build and served same-origin. */
const MOON_SOURCE = "/vendor/moon/mare.json";

/**
 * Tycho's longitude, which is also the meridian the lunar globe is permanently
 * aimed at: -11.22 is the first control-network centre of Tycho (USGS Gazetteer
 * of Planetary Nomenclature, feature 6163). Aiming the camera at it is what
 * keeps the face the viewer sees — and the caption beside the disc — from
 * rotating toward the far limb.
 */
const TYCHO_LNG = -11.22;

/** The map's own zoom floor, matching the `minZoom` its construction passes. */
const MOON_MIN_ZOOM = -4;

/**
 * How long the lunar globe may spend loading while it is visible. A style, a
 * same-origin GeoJSON and one WebGL context is a small job; eight visible
 * seconds means something is wrong, and the finale must not keep a hidden body
 * waiting on it. The bound is banked across `pause`, so a backgrounded tab
 * cannot burn it.
 */
const MOON_LOAD_BOUND_MS = 8000;

/**
 * The zoom that renders the lunar globe `diameter` px across on its square host.
 *
 * The world size a sphere of that diameter needs under the vendored v6 globe
 * projection is `2*pi*((1+sqrt(10))/6)*diameter`, and one zoom level doubles the
 * drawn size over a 512px base tile. Measured against the vendored runtime in a
 * browser, this inscribes the host: the disc comes out 56px on a 56px host and
 * 28px on a 28px one, within a pixel of each, so the limb touches the box and
 * the moon's size is the size the layout handed it.
 *
 * It is only correct because the load handler writes a camera after the
 * projection switch — see the note there; without that write the globe renders
 * in a fit state several times too large. There is still no runtime pixel
 * reading: a framebuffer readback in the frame path would cost far more than one
 * constant that was checked once, in a browser, against this exact bundle.
 */
const moonZoomFor = (diameter: number): number =>
  Math.log2((2 * Math.PI * ((1 + Math.sqrt(10)) / 6) * diameter) / 512);

/**
 * The coming-soon caption's element.
 *
 * It used to be a MapLibre Marker pinned to Tycho's lunar coordinates, which put
 * the label on the disc's lower surface rather than beside it — and a vendor
 * marker is positioned by a `transform` alone, with the `position: absolute` it
 * needs coming from the vendored stylesheet this page preloads but never links.
 * Left in normal flow, the caption was laid out *after* the canvas: measured on
 * production, its centre sat 80px below the disc's centre. It is placed against
 * the lunar box in the stylesheet instead — exact at every diameter, and not
 * something a projection can move.
 */
const moonCaption = (): HTMLElement => {
  const element = document.createElement("div");
  element.className = "atlas-moon-caption";
  const bar = document.createElement("span");
  bar.className = "atlas-moon-caption-bar";
  const label = document.createElement("span");
  label.className = "atlas-moon-label";
  label.textContent = "SOON";
  element.append(bar, label);
  return element;
};

/**
 * The one camera this module writes: the disc's centre, its size, and squared up
 * to north. Three call sites need it to compose identically — the load handler,
 * the frame path's resize and the re-assert on the map's own `resize` — because
 * two of them exist to undo a camera the vendored Map has already replaced, and a
 * camera that disagreed with itself would make the disc jump.
 *
 * The centre carries no phase. The globe is aimed at the Tycho meridian for every
 * frame of its life, so the face the viewer sees — and the caption pinned to it —
 * never rotates toward the far limb, where `opacityWhenCovered: 0` would take the
 * only "SOON" on the page away. The orbit still carries the figure across the
 * sky; nothing turns on its axis.
 */
const writeLunarCamera = (instance: MaplibreGl.Map, diameter: number): void => {
  instance.jumpTo({
    center: [TYCHO_LNG, 0],
    zoom: moonZoomFor(diameter),
    bearing: 0,
    pitch: 0,
  });
};

export const createAtlasBodies = (
  stage: HTMLElement,
  requestFrame: () => void,
  palette: AtlasBodiesPalette,
): AtlasBodies => {
  // Cached once, at creation: the frame loop writes inline geometry to these on
  // every tick, so a per-frame querySelector would put style resolution inside
  // every animation frame. A missing node is skipped throughout — a decorative
  // body that never made it into the markup must not affect the Earth's map.
  const moonNode = stage.querySelector<HTMLElement>(".atlas-moon");
  const moonHost = moonNode?.querySelector<HTMLElement>(".atlas-moon-map") ?? null;
  const issNode = stage.querySelector<HTMLElement>(".atlas-satellite--iss");
  const sputnikNode = stage.querySelector<HTMLElement>(".atlas-satellite--sputnik");

  let dead = false;
  let failed = false;
  let ready = false;
  let moon: MaplibreGl.Map | null = null;
  let caption: HTMLElement | null = null;
  // Last written size, and the last published overlap. The parent schedules a
  // frame for every scroll step and every rotation tick, and re-writing an
  // unchanged size would be pure cost; the overlap is cached in the same spirit,
  // because an attribute write invalidates style for a value that changes only
  // when the moon crosses the planet's limb.
  let lastDiameter = 0;
  let lastOverEarth = false;

  // The load bound, banked across pauses: only visible time is ever subtracted,
  // so a bound that was paused cannot expire while nobody could see the moon.
  let remainingMs = MOON_LOAD_BOUND_MS;
  let boundStartedAt = 0;
  let boundTimer: number | null = null;

  // The host is hidden until a globe-projected frame has actually rendered;
  // CSS keys that on the state below. There is no flat-SVG success state and no
  // fallback art — the rejected representation is gone from the page entirely.
  if (moonNode) moonNode.dataset.moonState = "loading";

  const disarmBound = (): void => {
    if (boundTimer === null) return;
    remainingMs = Math.max(0, remainingMs - (performance.now() - boundStartedAt));
    window.clearTimeout(boundTimer);
    boundTimer = null;
  };

  const armBound = (): void => {
    if (boundTimer !== null || dead || ready || failed) return;
    boundStartedAt = performance.now();
    boundTimer = window.setTimeout(() => {
      boundTimer = null;
      failMoon("the lunar globe did not render in time");
    }, remainingMs);
  };

  /**
   * Drops the lunar instance and its caption. Never throws: a decorative body
   * that fails must not take the parent's frame loop, or the Earth, down with it.
   */
  const release = (): void => {
    const instance = moon;
    moon = null;
    caption?.remove();
    caption = null;
    if (!instance) return;
    try {
      instance.remove();
    } catch (error) {
      console.error("[noko-moon] removing the lunar map failed", error);
    }
  };

  const failMoon = (reason: string, error?: unknown): void => {
    if (dead || failed) return;
    failed = true;
    disarmBound();
    if (moonNode) moonNode.dataset.moonState = "fallback";
    console.error("[noko-moon] lunar globe unavailable:", reason, error ?? "");
    release();
  };

  /**
   * Builds the lunar globe inside its host. Lazy by design: a second WebGL
   * context and a second canvas are not free, and the moon is only on screen
   * during the finale, so nothing is created until a frame actually asks for it.
   */
  const build = (vendor: ML, host: HTMLElement, diameter: number): void => {
    host.style.width = `${diameter}px`;
    host.style.height = `${diameter}px`;
    // MapLibre adds `.maplibregl-map` to this div, and that rule sets
    // `overflow: hidden`. The moon's caption has to be able to sit outside the
    // disc, so the host opts out; the stage still clips the moon's offscreen
    // entrance, which is what keeps it from arriving early from the right edge.
    host.style.overflow = "visible";

    let instance: MaplibreGl.Map;
    try {
      instance = new vendor.Map({
        container: host,
        // The style is inline, not a URL: a style URL would pull in glyphs and
        // tiles the moon has no use for — this orb is at most 56px, so fetching
        // a font set or a basemap to draw it would be pure waste. This asks for
        // exactly one same-origin source and three paint layers, so loading the
        // moon costs the committed mare asset and nothing else. The maria are
        // the real LROC outlines (see the map-assets `moon` build), not invented
        // crater artwork.
        style: {
          version: 8,
          sources: { mare: { type: "geojson", data: MOON_SOURCE } },
          layers: [
            {
              id: "moon-surface",
              type: "background",
              paint: { "background-color": palette.surface },
            },
            {
              id: "moon-mare",
              type: "fill",
              source: "mare",
              paint: { "fill-color": palette.basin },
            },
            {
              id: "moon-mare-outline",
              type: "line",
              source: "mare",
              paint: {
                "line-color": palette.outline,
                "line-opacity": 0.55,
                "line-width": 0.6,
              },
            },
          ],
        },
        center: [TYCHO_LNG, 0],
        zoom: moonZoomFor(diameter),
        bearing: 0,
        pitch: 0,
        minZoom: MOON_MIN_ZOOM,
        // Display-only, exactly like the Earth's map: no wheel, touch or drag.
        interactive: false,
        attributionControl: false,
        // Showing the lunar far side as a repeat of the near side would be a lie
        // about the Moon, and the globe projection has no seam to hide anyway.
        renderWorldCopies: false,
        // At 28-56px the limb is most of what is drawn: a hard edge matters more
        // here than it does on the Earth.
        canvasContextAttributes: { antialias: true },
      });
    } catch (error) {
      failMoon("map construction failed", error);
      return;
    }

    moon = instance;
    lastDiameter = diameter;

    instance.on("error", (event) => failMoon("style or source failed", event.error));
    instance
      .getCanvas()
      .addEventListener("webglcontextlost", () => failMoon("the WebGL context was lost"));

    instance.on("load", () => {
      if (dead || failed || moon !== instance) return;
      try {
        // Globe mode — must wait for the style: setProjection throws while the
        // style is still loading (v6 _checkLoaded).
        instance.setProjection({ type: "globe" });
      } catch (error) {
        failMoon("the globe projection failed", error);
        return;
      }

      // Write the camera here, not only from `render`. Measured in the browser:
      // after a projection switch the globe is drawn in a fit state — a fixed
      // fraction of the canvas, independent of the zoom it was constructed with
      // (132px on a 200px canvas at every zoom from -4 to 0) — and it stays that
      // way until a camera write lands. Without this one write the finale would
      // show a sphere several times the size of its box until the size happened
      // to change, and under reduced motion, where nothing else writes either, it
      // would never correct itself. One jumpTo is enough: the disc is the right
      // size on the very next frame.
      writeLunarCamera(instance, lastDiameter);

      // The same class of trap, one step later: a camera write issued in the
      // same turn as `resize()` does not stick. MapLibre derives the transform
      // for the new canvas from the camera it held before that write, so the
      // zoom silently reverts to the previous diameter's — measured as a 30px
      // host growing to 53.5px and leaving a 35px disc inside a 54px box. This
      // handler is now the only thing that lands a resize, because the frame
      // path's own write is the one that gets discarded. Re-asserting on the
      // map's own `resize` event is the one hook that runs after that transform
      // exists, and it is what keeps the disc inscribed when the parent is not
      // running frames (reduced motion), where no later `render` arrives.
      instance.on("resize", () => {
        if (dead || failed || moon !== instance) return;
        writeLunarCamera(instance, lastDiameter);
      });

      // Placed in the moon's own box, beside the disc. The caption is a child of
      // the figure rather than a marker on the map because the box IS the disc —
      // the renderer writes its size and the limb ring in the stylesheet already
      // relies on that — while a projected point is only as steady as the DOM the
      // vendored marker writes into. It arrives with the style, so the caption
      // and the globe it annotates appear together.
      if (moonNode) {
        caption = moonCaption();
        moonNode.append(caption);
      }

      // One-shot: the first frame rendered after the projection switch is the
      // first frame that actually draws the globe, so that is the moment the
      // moon may appear. A recurring render listener would be a second
      // perpetual animation loop, which this module deliberately does not own.
      instance.once("render", () => {
        if (dead || failed || moon !== instance) return;
        ready = true;
        disarmBound();
        if (moonNode) moonNode.dataset.moonState = "ready";
        // The parent owns the clock: ask it for the frame that positions the
        // freshly revealed disc rather than starting anything of our own.
        requestFrame();
      });
    });

    armBound();
  };

  const render: AtlasBodies["render"] = (layout, closing, vendor) => {
    if (dead) return;

    // The three placement boxes, verbatim from the geometry helper: position and
    // size both, which is what lets the moon's diameter and the spacecrafts'
    // scale change per breakpoint without a media query in the CSS.
    place(moonNode, layout.moon);
    place(issNode, layout.iss);
    place(sputnikNode, layout.sputnik);

    if (moonNode !== null && lastOverEarth !== layout.overEarth) {
      lastOverEarth = layout.overEarth;
      moonNode.dataset.moonOverEarth = String(layout.overEarth);
    }

    const diameter = layout.moon.width;
    if (moon === null && !failed && moonHost !== null && vendor !== null && closing > 0) {
      build(vendor, moonHost, diameter);
    }

    if (moon !== null && moonHost !== null) {
      const resized = diameter !== lastDiameter;
      if (resized) {
        moonHost.style.width = `${diameter}px`;
        moonHost.style.height = `${diameter}px`;
        moon.resize();
        // A size change is now the only reason this path writes: the load
        // handler lands the first camera, and the re-assert on the map's own
        // `resize` event is what actually holds the new zoom, because a
        // `jumpTo` issued in the same turn as `resize()` is discarded.
        writeLunarCamera(moon, diameter);
      }
      lastDiameter = diameter;
    }

    // A render only happens while the parent is running frames, which it does
    // only while the tab is visible, so a render is also the resume signal for a
    // bound that `pause` banked. `armBound` is a no-op while one is already
    // armed, so this costs nothing on the frames in between.
    if (moon !== null && !ready && !failed) armBound();
  };

  const pause = (): void => disarmBound();

  const dispose = (): void => {
    if (dead) return;
    dead = true;
    disarmBound();
    release();
  };

  return { render, pause, dispose };
};

/** Writes one rendered box as absolute stage pixels, the shape the geometry returns. */
const place = (node: HTMLElement | null, box: Placement): void => {
  if (!node) return;
  node.style.left = `${box.x}px`;
  node.style.top = `${box.y}px`;
  node.style.width = `${box.width}px`;
  node.style.height = `${box.height}px`;
};
